import type { QualityCheck, RFPContext, ComplianceMatrixRow } from "./types";
import type { EvaluationWeights } from "./types";

export interface ConsistencyInput {
  ctx: RFPContext;
  matrix: ComplianceMatrixRow[];
  sections: { id: string; body: string }[];
  weights: EvaluationWeights;
  slaDescriptions: string[];
  /** Red-gap keys the user has explicitly waived (honoured by issue_requirements). */
  waivedGaps?: string[];
}

/**
 * Consistency engine (spec §5.2).
 * Returns a list of checks with severity pass | warn | block.
 */
export function runConsistencyChecks(input: ConsistencyInput): QualityCheck[] {
  const checks: QualityCheck[] = [];
  const { ctx, matrix, sections, weights, slaDescriptions } = input;
  const waived = new Set(input.waivedGaps ?? []);
  const allText = sections.map((s) => s.body).join("\n").toLowerCase();

  // 1. Mandatory matrix ids present in Section 5 body
  const mandatory = matrix.filter((r) => r.mandatory);
  const s5 = sections.find((s) => s.id === "s05_matrix")?.body ?? "";
  const missingFromMatrix = mandatory.filter((r) => !s5.includes(r.id));
  if (missingFromMatrix.length === 0) {
    checks.push({ id: "matrix_all_mandatory", severity: "pass", label: "All mandatory rows in matrix", detail: `${mandatory.length} mandatory rows present in Section 5.` });
  } else {
    checks.push({ id: "matrix_all_mandatory", severity: "block", label: "Mandatory rows missing from Section 5", detail: missingFromMatrix.map((r) => r.id).join(", ") });
  }

  // 2. Weighting sums to 100
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  if (sum === 100) {
    checks.push({ id: "weights_100", severity: "pass", label: "Evaluation weights sum to 100", detail: `Total weight = ${sum}%.` });
  } else {
    checks.push({ id: "weights_100", severity: "block", label: "Evaluation weights do not sum to 100", detail: `Total weight = ${sum}% — must equal 100%.` });
  }

  // 3. Scorecard criteria map to matrix IDs or Sections 6/7/8
  const mapsToOther = matrix.some((r) => !(r.maps_to in weights));
  if (mapsToOther) {
    checks.push({ id: "scorecard_mapping", severity: "warn", label: "Some matrix rows map to unknown dimensions", detail: "Maps_to values referencing non-existent scorecard dimensions will be ignored at scoring." });
  } else {
    checks.push({ id: "scorecard_mapping", severity: "pass", label: "Matrix maps cleanly to scorecard dimensions", detail: `${matrix.length} rows map to known dimensions.` });
  }

  // 4. SLA in Section 8 consistent with matrix
  const slaMismatch = slaDescriptions.filter((s) => s && s.includes("≥") && !matrix.some((r) => r.requirement.toLowerCase().includes(s.split("≥")[0].trim().toLowerCase())));
  if (slaMismatch.length === 0) {
    checks.push({ id: "sla_consistent", severity: "pass", label: "Section 8 SLAs consistent with matrix", detail: "All qualitative SLAs trace to a matrix requirement." });
  } else {
    checks.push({ id: "sla_consistent", severity: "warn", label: "Section 8 introduces SLAs not in the matrix", detail: slaMismatch.join("; ") });
  }

  // 5. No invented numbers — scan for [TBD] or numbers not sourced
  const tbdCount = allText.split("[tbd]").length - 1;
  const playbookDefaults = allText.split("[playbook default").length - 1;
  if (tbdCount > 0 || playbookDefaults > 0) {
    // [PLAYBOOK DEFAULT] are author "yellow" notes — they must be cleared
    // before the pack can be issued (blocks). [TBD] is a normal open variable.
    const severity = playbookDefaults > 0 ? "block" : "warn";
    checks.push({
      id: "number_provenance",
      severity,
      label: severity === "block" ? "Author notes remain — resolve before issue" : "Document contains uncleared placeholders",
      detail: `${tbdCount} [TBD] and ${playbookDefaults} [PLAYBOOK DEFAULT] placeholders remain. Yellow author notes are never exported issue-ready.`,
    });
  } else {
    checks.push({ id: "number_provenance", severity: "pass", label: "No uncleared placeholders", detail: "No [TBD] / [PLAYBOOK DEFAULT] placeholders in generated text." });
  }

  // 6. Submission method matches process.issue_method
  const issueMethod = (ctx.process.issue_method || "email").toLowerCase();
  const methodMatch = sections.some((s) => s.body.toLowerCase().includes(issueMethod));
  if (methodMatch) {
    checks.push({ id: "issue_method_consistent", severity: "pass", label: "Submission method stated", detail: `Submissions to be received ${ctx.process.issue_method}.` });
  } else {
    checks.push({ id: "issue_method_consistent", severity: "warn", label: "Submission method not stated in body", detail: "Sections do not restate the issue method." });
  }

  // 7. No "log into Sensei" sentences
  if (allText.includes("log into sensei") || allText.includes("sensei portal")) {
    checks.push({ id: "no_sensei_portal", severity: "block", label: "Doc references a Sensei portal", detail: "Section 3 must describe email / Ariba / Coupa submission only — Sensei never invites suppliers." });
  } else {
    checks.push({ id: "no_sensei_portal", severity: "pass", label: "No supplier-facing Sensei portal references", detail: "Submission channels are external only." });
  }

  // 8. Red-list gaps
  const deadlineSet = !!ctx.process.deadline;
  const budgetOk = ctx.constraints.budget.known === true || ctx.constraints.budget.publish_to_bidders === false;
  if (deadlineSet && budgetOk && ctx.stakeholders.procurement_lead) {
    checks.push({ id: "red_list_cleared", severity: "pass", label: "Critical details resolved", detail: "Deadline, budget and procurement contact are set." });
  } else {
    const missing: string[] = [];
    if (!deadlineSet) missing.push("deadline");
    if (!budgetOk) missing.push("budget (known or explicitly not published)");
    if (!ctx.stakeholders.procurement_lead) missing.push("procurement contact");
    checks.push({ id: "red_list_cleared", severity: "block", label: "Red-list items open", detail: missing.join(", ") + " — resolve or waive in the context drawer." });
  }

  // 9. Legal stubs present
  const hasLegal = sections.some((s) => s.body.includes("[ATTACH: Client MSA"));
  if (hasLegal) {
    checks.push({ id: "legal_stubs", severity: "pass", label: "MSA / GT&C attach-stub present", detail: "Legal documents remain attach-stubs; no invented clauses." });
  } else {
    checks.push({ id: "legal_stubs", severity: "warn", label: "MSA attach-stub missing", detail: "Section 11 should reference the attached client MSA." });
  }

  // 10. Schedule of events present in Section 3
  const s3 = sections.find((s) => s.id === "s03_response")?.body ?? "";
  const hasSchedule = /##?\s*schedule of events/i.test(s3);
  if (hasSchedule) {
    checks.push({ id: "events_table_present", severity: "pass", label: "Schedule of events present", detail: "Section 3 includes the procurement timetable with event rows." });
  } else {
    checks.push({ id: "events_table_present", severity: "block", label: "Schedule of events missing", detail: "Section 3 must include the schedule of events (issue / Q&A / proposal due / presentations / award)." });
  }

  // 11. Every schedule-of-events row carries a date or an explicit [TBD]
  if (hasSchedule) {
    const start = s3.indexOf("Schedule of events");
    const rest = s3.slice(start);
    const endIdx = rest.indexOf("\n## ", rest.indexOf("\n") + 1);
    const block = endIdx > 0 ? rest.slice(0, endIdx) : rest;
    const rows = block.split("\n").filter((l) => l.startsWith("- "));
    const unresolved = rows.filter((r) => r.includes("[TBD]") || /—\s*$/.test(r));
    if (unresolved.length === 0) {
      checks.push({ id: "events_dates_filled", severity: "pass", label: "Schedule of events dated", detail: `${rows.length} event rows carry dates.` });
    } else {
      checks.push({ id: "events_dates_filled", severity: "warn", label: "Event dates unresolved", detail: `${unresolved.length} of ${rows.length} event rows are still [TBD] — resolved or waived in the context drawer.` });
    }
  }

  // 12. Issue-ready gate: proposal deadline AND validity days, or an explicit waiver
  const validityOk = ctx.commercial.price_validity_days > 0 || waived.has("price_validity");
  if (deadlineSet && validityOk) {
    checks.push({ id: "issue_requirements", severity: "pass", label: "Issue requirements met", detail: "Proposal deadline set and price validity confirmed (or explicitly waived)." });
  } else {
    const missing: string[] = [];
    if (!deadlineSet) missing.push("proposal deadline");
    if (!validityOk) missing.push("price validity days (or an explicit waiver)");
    checks.push({ id: "issue_requirements", severity: "block", label: "Issue requirements open", detail: missing.join(", ") + " — required before issue-ready." });
  }

  return checks;
}

export function gradePack(checks: QualityCheck[]): "draft" | "issue_ready" {
  const blocks = checks.filter((c) => c.severity === "block").length;
  return blocks === 0 ? "issue_ready" : "draft";
}

export function tbdCount(body: string): number {
  return (body.split("[TBD]").length - 1);
}